import React, { useState, useEffect } from 'react'
import "../../css/Home/mainBanner.css";
import mainBannerImg from "../../assets/img/mainBanner.png";
import SearchGroup from './searchGroup';
import PromotionBanner from './promotionBanner';
import { apiGet } from '../../utils/api';
import { BASE_IMAGE_URL } from "../Common-components/constants";
import { useNavigate } from 'react-router-dom';

const DEFAULT_BANNER_MEDIA = { src: mainBannerImg, type: 'image', href: null, isExternal: false };

export default function MainBanner({ bannerData, promoData, promoItemsData, cruiseInfoData, cruiseInfoItemsData }) {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth <= 651 : false);
  const [bannerMedia, setBannerMedia] = useState(DEFAULT_BANNER_MEDIA);
  const [isBannerResolved, setIsBannerResolved] = useState(false);
  const [promoBanner, setPromoBanner] = useState({PC: [],TA: [],MO: []});
  const [isPromoLoading, setIsPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState(null);
  const [cruiseInfoBanner, setCruiseInfoBanner] = useState({PC: [],TA: [],MO: []});
  const [cruiseInfoError, setCruiseInfoError] = useState(null);

  useEffect(() => {
    if (bannerData !== undefined) {
      if (bannerData && bannerData.src) {
        setBannerMedia(normalizeMainBannerMedia(bannerData, DEFAULT_BANNER_MEDIA));
      } else {
        setBannerMedia(DEFAULT_BANNER_MEDIA);
      }
      setIsBannerResolved(true);
      return;
    }
  }, [bannerData]);

  useEffect(() => {
    if (
      promoData !== undefined ||
      promoItemsData !== undefined ||
      cruiseInfoData !== undefined ||
      cruiseInfoItemsData !== undefined
    ) {
      const mapped = mapPromoBannerPayload(promoItemsData, promoData);
      const hasPromo = mapped.PC.length > 0 || mapped.TA.length > 0 || mapped.MO.length > 0;
      setPromoBanner(mapped);
      setPromoError(hasPromo ? null : '프로모션 배너 데이터가 없습니다.');

      const mappedCruiseInfo = mapPromoBannerPayload(cruiseInfoItemsData, cruiseInfoData);
      const hasCruiseInfo = mappedCruiseInfo.PC.length > 0 || mappedCruiseInfo.TA.length > 0 || mappedCruiseInfo.MO.length > 0;
      setCruiseInfoBanner(mappedCruiseInfo);
      setCruiseInfoError(hasCruiseInfo ? null : '크루즈정보 배너 데이터가 없습니다.');
      setIsPromoLoading(false);
      return;
    }
  }, [promoData, promoItemsData, cruiseInfoData, cruiseInfoItemsData]);

  useEffect(() => {
    if (
      bannerData !== undefined ||
      promoData !== undefined ||
      promoItemsData !== undefined ||
      cruiseInfoData !== undefined ||
      cruiseInfoItemsData !== undefined
    ) return;
    let isActive = true;

    const fetchPayload = async () => {
      if (!isActive) return;
      setIsPromoLoading(true);
      setPromoError(null);

      try {
        const res = await apiGet('/main/get_home_banner_payload.php');
        if (!isActive) return;

        if (res?.success && res?.data) {
          const {
            main_banner: mainBanner,
            promo_banners: promoBanners,
            promo_banner_items: promoBannerItems,
            cruise_info_banners: cruiseInfoBanners,
            cruise_info_banner_items: cruiseInfoBannerItems,
          } = res.data;

          if (mainBanner && mainBanner.src) {
            setBannerMedia(normalizeMainBannerMedia(mainBanner, DEFAULT_BANNER_MEDIA));
          } else {
            setBannerMedia(DEFAULT_BANNER_MEDIA);
          }
          setIsBannerResolved(true);

          const mapped = mapPromoBannerPayload(promoBannerItems, promoBanners);
          const hasPromo = mapped.PC.length > 0 || mapped.TA.length > 0 || mapped.MO.length > 0;
          setPromoBanner(mapped);
          setPromoError(hasPromo ? null : '프로모션 배너 데이터가 없습니다.');

          const mappedCruiseInfo = mapPromoBannerPayload(cruiseInfoBannerItems, cruiseInfoBanners);
          const hasCruiseInfo = mappedCruiseInfo.PC.length > 0 || mappedCruiseInfo.TA.length > 0 || mappedCruiseInfo.MO.length > 0;
          setCruiseInfoBanner(mappedCruiseInfo);
          setCruiseInfoError(hasCruiseInfo ? null : '크루즈정보 배너 데이터가 없습니다.');
        } else {
          setBannerMedia(DEFAULT_BANNER_MEDIA);
          setIsBannerResolved(true);
          setPromoBanner({ PC: [], TA: [], MO: [] });
          setPromoError('프로모션 배너 데이터가 없습니다.');
          setCruiseInfoBanner({ PC: [], TA: [], MO: [] });
          setCruiseInfoError('크루즈정보 배너 데이터가 없습니다.');
        }
      } catch (error) {
        console.error('메인 배너 로딩 실패:', error);
        if (isActive) {
          setBannerMedia(DEFAULT_BANNER_MEDIA);
          setIsBannerResolved(true);
          setPromoBanner({ PC: [], TA: [], MO: [] });
          setPromoError(error?.message || '프로모션 배너를 불러오지 못했습니다.');
          setCruiseInfoBanner({ PC: [], TA: [], MO: [] });
          setCruiseInfoError(error?.message || '크루즈정보 배너를 불러오지 못했습니다.');
        }
      } finally {
        if (isActive) {
          setIsPromoLoading(false);
        }
      }
    };

    fetchPayload();

    return () => {
      isActive = false;
    };
  }, [bannerData, promoData, promoItemsData, cruiseInfoData, cruiseInfoItemsData]);

  // 651px 이하에서는 SearchGroup 숨김
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 651);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const mediaToDisplay = isBannerResolved ? bannerMedia : DEFAULT_BANNER_MEDIA;
  const bannerVisibility = isBannerResolved ? 'visible' : 'hidden';
  const isMainBannerClickable = Boolean(mediaToDisplay?.href);

  const handleMainBannerClick = () => {
    navigateToBannerTarget(mediaToDisplay, navigate);
  };

  const handleMainBannerKeyDown = (event) => {
    if (!isMainBannerClickable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleMainBannerClick();
    }
  };

  return (
    <section className="main-banner-section">
      <div className='banner'>
        {isMobile && (
          <div className='banner-img'>
            {mediaToDisplay.type === 'video' ? (
              <video
                key={mediaToDisplay.src}
                src={mediaToDisplay.src}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                style={{ visibility: bannerVisibility, cursor: isMainBannerClickable ? 'pointer' : 'default' }}
                onClick={isMainBannerClickable ? handleMainBannerClick : undefined}
                onKeyDown={handleMainBannerKeyDown}
                role={isMainBannerClickable ? 'button' : undefined}
                tabIndex={isMainBannerClickable ? 0 : -1}
              />
            ) : (
              <img
                src={mediaToDisplay.src}
                alt="mainBanner"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                style={{ visibility: bannerVisibility, cursor: isMainBannerClickable ? 'pointer' : 'default' }}
                onClick={isMainBannerClickable ? handleMainBannerClick : undefined}
                onKeyDown={handleMainBannerKeyDown}
                role={isMainBannerClickable ? 'button' : undefined}
                tabIndex={isMainBannerClickable ? 0 : -1}
              />
            )}
          </div>
        )}

        <div className="banner-content">
          <p className='banner-text'>
            세상의 모든 크루즈<br />
            지금 여기서 와우하게 검색하세요.
          </p>

          <div className='banner-search'>
            <SearchGroup 
              moTitle={isMobile 
                ? '전 세계 크루즈 지금 와우하게 검색하기'
                : '조건에 맞는 크루즈 검색 하기' 
              }
            />
          </div>
        </div>

      </div>

      {/* 프로모션 배너 */}
      <PromotionBanner
        className="promotion-banner-wrapper--compact-bottom"
        promoBanners={promoBanner}
        promoStatus={{ loading: isPromoLoading, error: promoError }}
      />
      <PromotionBanner
        title="크루즈정보"
        promoBanners={cruiseInfoBanner}
        promoStatus={{ loading: isPromoLoading, error: cruiseInfoError }}
      />
    </section>
  )
}

function resolveMediaUrl(url) {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) {
    return `${BASE_IMAGE_URL}${url}`;
  }
  return `${BASE_IMAGE_URL}/${url}`;
}

function normalizeBoardType(value) {
  const type = String(value || '').toLowerCase();
  if (type === 'notice' || type === 'event') return type;
  return '';
}

function buildInternalHref(boardTypeRaw, postIdRaw) {
  const boardType = normalizeBoardType(boardTypeRaw);
  const postId = Number(postIdRaw);
  if (!boardType) return null;
  if (Number.isFinite(postId) && postId > 0) {
    return `/${boardType}/${postId}`;
  }
  return `/${boardType}`;
}

function normalizeHref(data) {
  const directHref = String(data?.href || '').trim();
  if (directHref) return directHref;
  const linkUrl = String(data?.link_url || '').trim();
  if (linkUrl) return linkUrl;
  return buildInternalHref(data?.link_board_type, data?.link_post_id);
}

function normalizeMainBannerMedia(data, fallbackMedia) {
  const resolvedSrc = resolveMediaUrl(data?.src);
  const href = normalizeHref(data);
  const isExternal = Boolean(Number(data?.is_external)) || /^https?:\/\//i.test(String(href || ''));
  return {
    src: resolvedSrc || fallbackMedia.src,
    type: data?.type === 'video' ? 'video' : 'image',
    href: href || null,
    isExternal,
  };
}

function normalizePromoBannerItem(item) {
  if (typeof item === 'string') {
    const src = resolveMediaUrl(item);
    if (!src) return null;
    return { src, href: null, isExternal: false };
  }

  if (!item || typeof item !== 'object') {
    return null;
  }

  const src = resolveMediaUrl(item.src || item.img_url || '');
  if (!src) return null;
  const href = normalizeHref(item);
  const isExternal = Boolean(Number(item.is_external)) || /^https?:\/\//i.test(String(href || ''));
  return {
    src,
    href: href || null,
    isExternal,
  };
}

function mapPromoList(items, legacy) {
  if (Array.isArray(items)) {
    return items.map(normalizePromoBannerItem).filter(Boolean);
  }
  if (Array.isArray(legacy)) {
    return legacy.map(normalizePromoBannerItem).filter(Boolean);
  }
  return [];
}

function mapPromoBannerPayload(itemPayload, legacyPayload) {
  const items = itemPayload && typeof itemPayload === 'object' ? itemPayload : {};
  const legacy = legacyPayload && typeof legacyPayload === 'object' ? legacyPayload : {};
  const webList = mapPromoList(items.w, legacy.w);
  return {
    PC: webList,
    TA: webList,
    MO: webList,
  };
}

function navigateToBannerTarget(item, navigate) {
  const href = String(item?.href || '').trim();
  if (!href) return;
  if (item?.isExternal || /^https?:\/\//i.test(href)) {
    window.location.assign(href);
    return;
  }
  navigate(href);
}
